package com.iregained.site.components.sections.about.sections

import androidx.compose.runtime.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Editorial Fade (Very slow opacity change)
@Composable
private fun Modifier.editorialFade(isVisible: Boolean, delayMs: Int = 0): Modifier {
    return this.opacity(if (isVisible) 1 else 0)
        .transition(Transition.of("opacity", 1.5.s, TransitionTimingFunction.EaseOut, delayMs.ms))
}

@Composable
fun AboutFounderLetterSection(data: AboutFounderLetterData = defaultAboutFounderLetterData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "about-founder-letter-section"
    var isVisible by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
            .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
            .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                .justifyContent(JustifyContent.Center).toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element); onDispose { observer.disconnect() }
                }
            }) {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1200.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SectionHeader(
                    text = data.sectionTitle,
                    fontSize = 32.px,
                    lineWidthPercent = 25.percent,
                    modifier = Modifier.margin(bottom = 50.px).editorialFade(isVisible, 0),
                    alignment = Alignment.CenterHorizontally
                )

                Div(
                    attrs = SubheadlineTextStyle.toModifier().fontSize(18.px).lineHeight(1.5)
                        .color(palette.custom.darkText).textAlign(TextAlign.Start).editorialFade(isVisible, 200)
                        .toAttrs {
                            ref { htmlElement -> htmlElement.innerHTML = data.founderLetter; onDispose {} }
                        })
            }
        }
    }
}

/**
 * CMS CONTENT & MEDIA SPECS FOR FOUNDER LETTER:
 * * @field imagePath:
 * - Aspect Ratio: 4:3 (Landscape) or 3:4 (Portrait) or 1:1 (Square).
 * - Recommended Dimensions: Minimum 900px wide.
 * - Format: WebP or high-quality JPEG (Max size: ~250KB).
 * - Behavior: On desktop, this image becomes "sticky" and floats gracefully alongside
 * the text as the user scrolls. A portrait or square image works beautifully here.
 * * @field founderLetterHtml:
 * - Format: Raw HTML string.
 * - Requirements: Text MUST be wrapped in standard HTML paragraph tags. Add an inline
 * style like `<p style="margin-bottom: 20px;">` to ensure proper visual breathing room.
 * - Length Limit: None. The sticky-image layout is specifically designed to handle
 * long-form editorial text without causing UI issues.
 */
data class AboutFounderLetterData(
    val sectionId: String? = null,
    val sectionTitle: String = "A Letter From Our Founder",
    val imagePath: String = Res.Images.About.Origin.ORIGIN_IMG, // Use a great photo of the founder here!
    val imageAlt: String = "Vineet Johnson, Founder and CEO of IRegained",
    val founderLetter: String = """
        <p style="margin-top: 0px; margin-bottom: 0px;">Ranjit, my co-founder, and I met in 1986. We attended the same Sunday school, same school and played in the same soccer team. Eventually, we both ended up studying Physical Therapy in the same college, Christian Medical College (CMC) Vellore – a prestigious medical school in India.</p>
        <p style="margin-top: 0px; margin-bottom: 0px;">Our curiosity led us to question what we did as part of therapeutic protocols we were taught to use to treat patients with neurological disorders. In fact, this led us to question how body movement was controlled by the brain, and how this control pattern is disrupted or altered as a result of disease of injury? And what parameters could assist in helping to create a relearning pathway or to reestablish damaged neuronal circuitry by leveraging the human brain’s innate ability to heal itself -- neuroplasticity? Answering these questions to some extent allowed us to focus on structuring therapeutic protocols that would allow a patient’s brain to regain control over their hand after a stroke or brain injury.</p>
        <p style="margin-top: 0px; margin-bottom: 0px;">While at present, the world has achieved huge strides in using technology to make lives easier and more productive, yet individuals’ post-stroke continues to suffer from disability and have to live with their disability for the rest of their lives. We want to change that. We want to make therapy affordable, accessible and efficient so that any one who needs the therapy should be able to access a device, receive effective hand function therapy, and once again become a contributing member in society.</p>
        <p style="margin-top: 0px; margin-bottom: 0px;">Our efforts and research have resulted in the development, clinical validation and market launch of a truly innovative system, "MyHand<sup>®</sup>", that can address the rehabilitation needs and improve hand function in individuals with brain trauma from stroke or injury. After building several of the Alpha and Beta prototypes of our device and testing them with our clinical collaborators, as well as validating them in multiple clinical studies, we have launched the MyHand<sup>®</sup> System in market. Moreover, we have designed a set of 16 proprietary function-oriented protocols (i.e. programed exercises that are delivered by our MyHand<sup>®</sup> system) that target specific tasks to effectively leverage the benefits of neuroplasticity.</p>
        <p style="margin-top: 0px; margin-bottom: 0px;">Our current MyHand<sup>®</sup> system will be used by therapists in rehabilitation centers (Physiotherapy clinics and rehab hospitals) and our future variant will be designed and priced such that stroke or brain injury patients will be able to use the device in the comfort of their home independently, under the supervision of their therapist. In subsequent generations of the System, we plan to add telemedicine and AI to further enhance the customization of hand function therapy.</p>
        <p style="margin-top: 10px; margin-bottom: 0px; font-size: 18px; font-weight: 600;">&mdash; Vineet Benjamin K. Johnson</p>
    """.trimIndent()
)

val defaultAboutFounderLetterData = AboutFounderLetterData()